import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";

const links = [
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Our Work", to: "/work" },
  { label: "About", to: "/about" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goContact = () => {
    setOpen(false);
    if (pathname === "/") {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#contact");
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled || pathname !== "/" ? "glass-nav border-b border-[#2E1B16]/10" : ""}`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo"><Logo /></Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={`text-sm font-semibold transition-colors relative group ${pathname.startsWith(l.to) ? "text-[#2E1B16]" : "text-[#2E1B16]/60 hover:text-[#2E1B16]"}`}
            >
              {l.label}
              <span className={`absolute -bottom-1.5 left-0 h-px bg-[#38BDF8] transition-all duration-300 ${pathname.startsWith(l.to) ? "w-full" : "w-0 group-hover:w-full"}`} />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={goContact}
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center rounded-full bg-[#2E1B16] text-[#FAF7F2] text-sm font-semibold px-5 py-2.5 hover:bg-[#38BDF8] hover:text-[#2E1B16] transition-colors duration-300"
          >
            Get in touch
          </button>
          <button className="md:hidden text-[#2E1B16]" onClick={() => setOpen((o) => !o)} data-testid="nav-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden glass-nav border-b border-[#2E1B16]/10"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {links.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-left text-[#2E1B16] font-semibold" data-testid={`nav-mobile-${l.label.toLowerCase().replace(" ", "-")}`}>
                  {l.label}
                </Link>
              ))}
              <button onClick={goContact} className="text-left text-[#38BDF8] font-semibold" data-testid="nav-mobile-cta">
                Get in touch →
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
